
import React from "react";
import Layout from "@/components/layout/Layout";
import { NursingHomeInquiryTest } from "@/components/test/NursingHomeInquiryTest";
import { EmailAnalyticsDemo } from "@/components/test/EmailAnalyticsDemo";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { AlertTriangle, ExternalLink, Library } from "lucide-react";
import { SectionHeader } from "@/components/ui/section-header";

export default function EmailTestPage() {
  return (
    <Layout>
      <div className="container py-12">
        <SectionHeader
          title="Email Testing Center"
          subtitle="Test and monitor your transactional emails"
          className="mb-8"
        />
        
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>Email Configuration</CardTitle>
            <CardDescription>Ensure your email setup is correctly configured</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <Alert>
              <AlertDescription>
                <div className="flex flex-col space-y-2">
                  <p><strong>From Address:</strong> hello@updates.grandpal.co</p>
                  <p><strong>Reply-To:</strong> brian@grandpal.co</p>
                  <div className="flex items-center gap-2">
                    <strong>Verify Domain:</strong> 
                    <a 
                      href="https://resend.com/domains" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:underline flex items-center"
                    >
                      Resend Domain Settings <ExternalLink className="h-3 w-3 ml-1" />
                    </a>
                  </div>
                </div>
              </AlertDescription>
            </Alert>
            
            <Alert variant="default" className="bg-amber-50 border-amber-200">
              <AlertTriangle className="h-4 w-4 text-amber-600" />
              <AlertDescription className="text-amber-800">
                All emails use shared components for consistent styling and easy maintenance.
              </AlertDescription>
            </Alert>
          </CardContent>
        </Card>
        
        <Tabs defaultValue="templates">
          <TabsList className="mb-4">
            <TabsTrigger value="templates">Email Templates</TabsTrigger>
            <TabsTrigger value="analytics">Email Analytics</TabsTrigger>
            <TabsTrigger value="components">Component Library</TabsTrigger>
          </TabsList>
          
          <TabsContent value="templates">
            <NursingHomeInquiryTest />
          </TabsContent>
          
          <TabsContent value="analytics">
            <Card>
              <CardHeader>
                <CardTitle>Email Performance Dashboard</CardTitle>
                <CardDescription>Monitor delivery and engagement metrics</CardDescription>
              </CardHeader>
              <CardContent>
                <EmailAnalyticsDemo />
              </CardContent>
            </Card>
          </TabsContent>
          
          <TabsContent value="components">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Library className="h-5 w-5" />
                  Email Components Library
                </CardTitle>
                <CardDescription>Shared components for consistent email design</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="prose max-w-none">
                  <h3 className="text-lg font-medium mt-0">Available Components</h3>
                  <ul className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    <li><code>EmailHeader</code> - Logo and branding</li>
                    <li><code>EmailFooter</code> - Copyright and unsubscribe</li>
                    <li><code>EmailButton</code> - Action buttons</li>
                    <li><code>EmailSection</code> - Content containers</li>
                    <li><code>EmailDivider</code> - Visual separators</li>
                    <li><code>EmailInfoBox</code> - Highlighted information</li>
                    <li><code>EmailFeatureList</code> - Feature highlights</li>
                    <li><code>EmailStyles</code> - Shared style constants</li>
                  </ul>
                  
                  <h3 className="text-lg font-medium">Implementation</h3>
                  <p>These components are located in <code>supabase/functions/_shared/emails/components/</code> and can be imported into any email template.</p>
                  
                  <div className="bg-gray-100 p-4 rounded-md">
                    <pre className="text-sm overflow-auto">
{`import { EmailHeader } from "./components/EmailHeader.tsx";
import { EmailFooter } from "./components/EmailFooter.tsx";
import { EmailButton } from "./components/EmailButton.tsx";
import { styles } from "./components/EmailStyles.tsx";

// Then in your template:
<Container>
  <EmailHeader />
  <Text style={styles.text}>Your content here</Text>
  <EmailButton href="https://example.com">Click Me</EmailButton>
  <EmailFooter year={2023} />
</Container>`}
                    </pre>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
        
        <div className="mt-8">
          <Alert>
            <AlertDescription className="flex items-center gap-2">
              <ExternalLink className="h-4 w-4" />
              <span>
                View detailed analytics in your{" "}
                <a 
                  href="https://resend.com/overview" 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="text-blue-600 hover:underline font-medium"
                >
                  Resend Dashboard
                </a>
              </span>
            </AlertDescription>
          </Alert>
        </div>
      </div>
    </Layout>
  );
}
